import { Edge } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { i18next } from '@frontend/i18n';
import { NodeStateWithAdditionalData } from '../../call-route-details/add-step-panel/types';
import { NodeAction, NodeActionType } from '../../types';
import { logError } from '../utils';
import { hasDownStreamSteps, isAssociativeNode, isTerminalNode } from './node-utils';

/**
 * Determines whether a warning should be shown when editing a node.
 * The states to show a warning are:
 *
 * - non-associative step w/ children --> associative step
 *    The user is trying to change a non-associative node to an associative node and the non-associative node has
 *    downstream steps (grandchildren in this case since the direct children are meta nodes such as dialOptions,
 *    "fallback", "open", "closed" and "break" nodes).
 *
 * - associative step w/ children --> non-associative step
 *    The user is trying to change an associative node to a non-associative node and the associative node has downstream steps.
 *
 * - associative step w/ children --> associative step
 *    The user is trying to change an associative node to another associative node and the original associative node has downstream steps.
 *
 * - non-associative step w/ children --> non-associative step and is terminal
 *    The user is trying to change a non-associative node to another non-associative node but the new step is a terminal step
 *    and the original non-associative node has downstream steps
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being edited.
 * @param {NodeStateWithAdditionalData} params.newNodeData - The new editing data of the node.
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 * ```
 */
export const shouldShowWarningForEdit = ({
  nodeId,
  newNodeData,
  nodes,
  edges,
}: {
  nodeId: string;
  newNodeData: NodeStateWithAdditionalData;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const editingNode = nodes.find((node) => node.id === nodeId);
  if (!editingNode) {
    logError('Could not find editing node in shouldShowWarningForEdit: PC-8djf032l', 'shouldShowWarningForEdit', {
      nodeId,
      nodes: nodes.map((n) => n.id),
    });
    return false;
  }

  const newNodeType = newNodeData.nodeState.type;

  // If the node type has not changed, then skip the logic to show a warning unless the type is a forwarding number
  // since the forwarding number node could have changed it's ring duration which needs to be considered.
  if (editingNode.type === newNodeType && !(newNodeType === CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER)) {
    return false;
  }

  const isAssociative = isAssociativeNode(editingNode.type);

  const hasChildrenSteps = hasDownStreamSteps({ nodeId: nodeId, nodes, edges });

  const newTypeIsAssociative = isAssociativeNode(newNodeType);
  const newNodeIsTerminal = isTerminalNode(newNodeData.nodeState);

  if (!isAssociative && hasChildrenSteps && newTypeIsAssociative) {
    return true;
  }

  if (isAssociative && hasChildrenSteps && !newTypeIsAssociative) {
    return true;
  }

  if (isAssociative && hasChildrenSteps && newTypeIsAssociative) {
    return true;
  }

  if (!isAssociative && hasChildrenSteps && !newTypeIsAssociative && newNodeIsTerminal) {
    return true;
  }

  return false;
};

/**
 * Determines whether a warning should be shown when adding a new node to the call routes.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.extendingNodeId - The ID of the node being extended.
 * @param {NodeStateWithAdditionalData} params.newNodeData - The state of the new node being added.
 * @param {Node[]} params.nodes - The list of all nodes in the call routes.
 * @param {Edge[]} params.edges - The list of all edges in the call routes.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForAdd = ({
  extendingNodeId,
  newNodeData,
  nodes,
  edges,
}: {
  extendingNodeId: string;
  newNodeData: NodeStateWithAdditionalData;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const extendingNode = nodes.find((node) => node.id === extendingNodeId);
  if (!extendingNode) {
    logError('Could not find extending node in shouldShowWarningForAdd: PC-9ei3kfg', 'shouldShowWarningForAdd', {
      extendingNodeId,
      nodes: nodes.map((n) => n.id),
    });
    return false;
  }

  const newNodeType = newNodeData.nodeState.type;

  const hasChildrenSteps = hasDownStreamSteps({ nodeId: extendingNodeId, nodes, edges });
  const newTypeIsAssociative = isAssociativeNode(newNodeType);
  const isTerminal = isTerminalNode(newNodeData.nodeState);

  if (hasChildrenSteps && (newTypeIsAssociative || isTerminal)) {
    return true;
  }

  return false;
};

/**
 * Determines whether a warning should be shown when deleting a node.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.deletingNodeId - The ID of the node being deleted.
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForDelete = ({
  deletingNodeId,
  nodes,
  edges,
}: {
  deletingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const deletingNode = nodes.find((node) => node.id === deletingNodeId);
  if (!deletingNode) {
    logError('Could not find deleting node: PC-5mf83k', 'shouldShowWarningForDelete', {
      deletingNodeId,
      nodes: nodes.map((n) => n.id),
    });
    return false;
  }

  const withDownStreamSteps = hasDownStreamSteps({ nodeId: deletingNodeId, nodes, edges });
  const isAssociative = isAssociativeNode(deletingNode.type);

  if (isAssociative && withDownStreamSteps) {
    return true;
  } else if (deletingNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS && withDownStreamSteps) {
    return true;
  }

  return false;
};

/**
 * Determines whether a warning should be shown based on the provided action type and node details.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being acted upon.
 * @param {NodeTypes} [params.newNodeData] - The new data of the node, if applicable (the updated data when editing a node or the data when adding a new node).
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @param {NodeActionType} params.actionType - The type of action being performed on the node.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarning = ({
  nodeId,
  newNodeData,
  nodes,
  edges,
  actionType,
}: {
  nodeId: string;
  newNodeData?: NodeStateWithAdditionalData;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  actionType: NodeActionType;
}): boolean => {
  if (actionType === NodeAction.Edit && newNodeData) {
    return shouldShowWarningForEdit({ nodeId: nodeId, newNodeData, nodes, edges });
  } else if (actionType === NodeAction.Add && newNodeData) {
    return shouldShowWarningForAdd({ extendingNodeId: nodeId, newNodeData, nodes, edges });
  } else if (actionType === NodeAction.Delete) {
    return shouldShowWarningForDelete({ deletingNodeId: nodeId, nodes, edges });
  } else {
    logError('Invalid action type or missing new node data: PC-s9d0f4', 'shouldShowWarning', {
      nodeId,
      actionType,
    });
    return false;
  }
};

/**
 * Takes a node object and runs certain checks on it to determine if any warnings should be displayed.
 *
 * @param node - The node object containing the call object to evaluate.
 * @returns A localized warning message if conditions are met, or an empty string otherwise.
 */
export const getNodeWarning = (node: CallRouteV1.Types.Node) => {
  if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP) {
    if ((node.callObject.callGroupExpansion.deviceNames?.length ?? 0) < 1) {
      return i18next.t('0 Devices in Call Group', { ns: 'phone' });
    }
  }

  if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE) {
    if ((node.callObject.callQueueExpansion.deviceNames?.length ?? 0) < 1) {
      return i18next.t('0 Devices in Call Queue', { ns: 'phone' });
    }
  }

  if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE) {
    if (!node.callObject.phoneTreeExpansion.promptMediaItemId) {
      return i18next.t('Needs audio prompt', { ns: 'phone' });
    }
  }

  return '';
};
